import { useEffect, useState } from 'react'
import { Modal, ModalHeader, ModalBody, Container, Col, Row, UncontrolledTooltip } from 'reactstrap'
import { InputContainer, TextEditor } from 'components/ideal-core-ui'
import Select from 'react-select'
import cn from 'classnames'
import s from './EmailContactModal.scss'
import { isAdmin } from 'utils/authService'
import { EmailFiles } from 'components/common/EmailFiles'
import { EMAIL_SPLIT_MODE, EMAIL_LEADS_SPLIT_MODE } from 'utils/constants'
import { FaInfoCircle } from 'react-icons/fa'

const EmailContactModal = ({
  users,
  proposalId,
  fromUser,
  toContact,
  ccContacts,
  bccContacts,
  allCCContacts,
  subject,
  emailBody,
  toggleEmailContactModal,
  showEmailContactModal,
  sendEmail,
  changeContacts,
  changeFromContact,
  setSubject,
  template,
  templates,
  setEmailTemplate,
  emailTemplateType,
  uploadFiles,
  files,
  removeFile,
  removeContact,
  defaultEmailSplitMode,
  app
}) => {
  const [splitMode, setSplitMode] = useState(
    app === 'leads' ? Object.keys(EMAIL_SPLIT_MODE)[0] : Object.keys(EMAIL_SPLIT_MODE)[1]
  )

  useEffect(() => {
    if (defaultEmailSplitMode) {
      setSplitMode(defaultEmailSplitMode)
    }
  }, [defaultEmailSplitMode])

  return (
    <Modal
      keyboard={false}
      backdrop="static"
      isOpen={showEmailContactModal}
      className={s.contactModal}
      size="xl"
    >
      <ModalHeader className={s.modalHeader}>Send Email</ModalHeader>
      <ModalBody>
        <div className={s.main}>
          <Container>
            <div className={s.emailType}>
              <span>How would you like emails to be sent?</span>
              <input
                type="radio"
                checked={splitMode === Object.keys(EMAIL_SPLIT_MODE)[0] || false}
                onChange={() => {
                  setSplitMode(Object.keys(EMAIL_SPLIT_MODE)[0])
                }}
              />{' '}
              {EMAIL_SPLIT_MODE[Object.keys(EMAIL_SPLIT_MODE)[0]]}
              <FaInfoCircle id="Type1" className={s.tooltip} />
              <UncontrolledTooltip placement="top" target="Type1">
                Works like a BCC. Use this option when sending email to people from different
                companies and you don&apos;t want a recipient to see everyone else on the list. Best
                option for mass email campaigns.
              </UncontrolledTooltip>
              {app !== 'leads' && (
                <>
                  <input
                    type="radio"
                    checked={splitMode === Object.keys(EMAIL_SPLIT_MODE)[1] || false}
                    onChange={() => {
                      setSplitMode(Object.keys(EMAIL_SPLIT_MODE)[1])
                    }}
                  />{' '}
                  {EMAIL_SPLIT_MODE[Object.keys(EMAIL_SPLIT_MODE)[1]]}
                  <FaInfoCircle id="Type2" className={s.tooltip} />
                  <UncontrolledTooltip placement="top" target="Type2">
                    Use this option when you want to group contacts from the same company so they
                    see which colleagues were sent this email. If you selected contacts from more
                    than 1 company, they will be grouped accordingly.
                  </UncontrolledTooltip>
                </>
              )}
              <input
                type="radio"
                checked={splitMode === Object.keys(EMAIL_SPLIT_MODE)[2] || false}
                onChange={() => {
                  setSplitMode(Object.keys(EMAIL_SPLIT_MODE)[2])
                }}
              />{' '}
              {app === 'leads'
                ? EMAIL_LEADS_SPLIT_MODE[Object.keys(EMAIL_LEADS_SPLIT_MODE)[0]]
                : EMAIL_SPLIT_MODE[Object.keys(EMAIL_SPLIT_MODE)[2]]}
              <FaInfoCircle id="Type3" className={s.tooltip} />
              <UncontrolledTooltip placement="top" target="Type3">
                Use this option when you want each recipient to see everyone else on the email.
                Please note that this option is not appropriate for email campaigns or mass
                emailing.
              </UncontrolledTooltip>
            </div>
            <Row>
              <Col lg="12">
                <div className={s.headerItem}>
                  <label className={s.toLabel}>To:</label>
                  {Array.isArray(toContact) ? (
                    <ul className={s.selectedContactsContainer}>
                      {toContact.map(contact => (
                        <li key={contact.id}>
                          <span className={s.contact}>
                            {contact.firstName} {contact.lastName}
                          </span>
                          {toContact.length > 1 && (
                            <span
                              className={s.removeContact}
                              onClick={() => {
                                removeContact(contact.id)
                              }}
                            >
                              &times;
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className={s.toContactInfo}>
                      {toContact ? `${toContact.firstName} ${toContact.lastName}` : ''}
                    </span>
                  )}
                </div>
              </Col>
            </Row>
            <Row>
              <Col lg="6">
                <div className={s.headerItem}>
                  {isAdmin() && (
                    <div className={s.item}>
                      <label>From:</label>
                      <Select
                        onChange={value => changeFromContact(value)}
                        options={users}
                        value={fromUser}
                        className={cn(s.itemSelect, s.fromSelect)}
                      />
                    </div>
                  )}
                  {!Array.isArray(toContact) && (
                    <div className={s.item}>
                      <label>CC:</label>
                      <Select
                        onChange={value => changeContacts(value, 'ccContacts')}
                        options={allCCContacts}
                        value={ccContacts}
                        isMulti
                        className={cn(s.itemSelect, s.ccSelect)}
                      />
                    </div>
                  )}
                </div>
              </Col>
              <Col lg="6">
                <div className={s.headerItem}>
                  <div className={cn(s.item, s.leftSideItem)}>
                    <label>BCC:</label>
                    <InputContainer
                      type="text"
                      text={bccContacts}
                      placeholder="Email addresses separated by , or ;"
                      cb={value => changeContacts(value, 'bccContacts')}
                      style={{ display: 'inline-block', width: '70%', marginLeft: '5%' }}
                      classname={s.itemInput}
                    />
                  </div>
                </div>
              </Col>
            </Row>
            {((!proposalId && !emailTemplateType && !Array.isArray(toContact)) ||
              app === 'leads') && (
              <Row>
                <Col lg="6">
                  <div className={s.headerItem}>
                    <div className={s.item}>
                      <label>Email Template:</label>
                      <Select
                        onChange={value => setEmailTemplate(value)}
                        options={templates}
                        value={template}
                        className={cn(s.itemSelect, s.templateSelect)}
                      />
                    </div>
                  </div>
                </Col>
              </Row>
            )}
            <Row>
              <Col lg="12">
                <div className={s.headerItem}>
                  <div className={s.item}>
                    <label className={s.subjectLabel}>Email Subject:</label>
                    <InputContainer
                      type="text"
                      text={subject}
                      placeholder="Email Subject"
                      cb={value => setSubject(value)}
                      style={{ display: 'inline-block', width: '88%', marginLeft: '2%' }}
                      classname={s.itemInput}
                    />
                  </div>
                </div>
              </Col>
            </Row>
            <Row>
              <Col lg="12">
                <div className={s.headerItem}>
                  <div className={s.item}>
                    <label className={s.attachmentsLabel}>Attachments:</label>
                    <div
                      style={{ display: 'inline-block', width: '88%', marginLeft: '2%' }}
                      className={s.attachmentsContainer}
                    >
                      <button className={s.uploader}>Choose Files</button>
                      <input
                        type="file"
                        onChange={uploadFiles}
                        className={s.uploadInput}
                        multiple
                      />
                      {files.length > 0 && <EmailFiles removeFile={removeFile} files={files} />}
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
            <Row>
              <Col lg="12">
                <TextEditor
                  template={emailBody}
                  height="400"
                  actions={[
                    {
                      text: 'Send Email',
                      cb: (template, title, subject, destroyEditor) => {
                        sendEmail(template, destroyEditor, splitMode)
                      },
                      className: s.saveBtn
                    },
                    {
                      text: 'Cancel',
                      cb: (content, title, subject, destroyEditor) => {
                        destroyEditor && destroyEditor()
                        toggleEmailContactModal()
                      }
                    }
                  ]}
                />
              </Col>
            </Row>
          </Container>
        </div>
      </ModalBody>
    </Modal>
  )
}

export default EmailContactModal
